import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastProvider } from './components';
import { ErrorBoundary } from './components/ErrorBoundary';
import { QueryProvider } from './lib/query-client';
import { NotificationProvider } from './contexts';
import { PublicLayout, AuthLayout, AccountLayout, AdminLayout } from './layouts';
import { RequireAdmin, RequireAuth } from './routes/guards';

import './index.css';

// Public
const LandingPage = lazy(() => import('./pages/Landing/LandingPage'));
const JastipBelanjaPage = lazy(() => import('./pages/Jastip/JastipBelanjaPage'));
const JastipTerkirimPage = lazy(() => import('./pages/Jastip/JastipTerkirimPage'));
const CekHargaPage = lazy(() => import('./pages/CekHarga/CekHargaPage'));
const TokoEtalasePage = lazy(() => import('./pages/Toko/TokoEtalasePage'));
const TokoKoleksiDetailPage = lazy(() => import('./pages/Toko/TokoKoleksiDetailPage'));
const TokoDigitalDetailPage = lazy(() => import('./pages/Toko/TokoDigitalDetailPage'));
const TokoTitipLisensiPage = lazy(() => import('./pages/Toko/TokoTitipLisensiPage'));
const TokoBayarPage = lazy(() => import('./pages/Toko/TokoBayarPage'));
const LacakPage = lazy(() => import('./pages/Lacak/LacakPage'));
const SyaratPage = lazy(() => import('./pages/Legal/SyaratPage'));
const PrivasiPage = lazy(() => import('./pages/Legal/PrivasiPage'));
const RefundPage = lazy(() => import('./pages/Legal/RefundPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Auth
const LoginPage = lazy(() => import('./pages/Auth/LoginPage'));
const DaftarPage = lazy(() => import('./pages/Auth/DaftarPage'));

// Account
const AkunRingkasanPage = lazy(() => import('./pages/Akun/AkunRingkasanPage'));
const AkunPesananPage = lazy(() => import('./pages/Akun/AkunPesananPage'));
const AkunPesananDetailPage = lazy(() => import('./pages/Akun/AkunPesananDetailPage'));
const AkunPenawaranListPage = lazy(() => import('./pages/Akun/AkunPenawaranListPage'));
const AkunPenawaranPage = lazy(() => import('./pages/Akun/AkunPenawaranPage'));
const AkunLisensiPage = lazy(() => import('./pages/Akun/AkunLisensiPage'));
const AkunPengaturanPage = lazy(() => import('./pages/Akun/AkunPengaturanPage'));

// Admin — one dynamic import for all nine screens, so admin code ships as its
// own chunk and never rides along in the public bundle (CONTRACT.md §2.4).
const adminChunk = () => import('./pages/Admin/admin');
const AdminAntrianPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminAntrianPage })));
const AdminPesananPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminPesananPage })));
const AdminPesananDetailPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminPesananDetailPage })));
const AdminPenawaranListPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminPenawaranListPage })));
const AdminPenawaranPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminPenawaranPage })));
const AdminTokoPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminTokoPage })));
const AdminTokoBaruPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminTokoBaruPage })));
const AdminTokoEditPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminTokoEditPage })));
const AdminLisensiPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminLisensiPage })));
const AdminLisensiKirimPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminLisensiKirimPage })));
const AdminTitipLisensiPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminTitipLisensiPage })));
const AdminPembayaranPage = lazy(() => adminChunk().then((m) => ({ default: m.AdminPembayaranPage })));

function App() {
  return (
    <ErrorBoundary>
      <QueryProvider>
        <NotificationProvider>
          <ToastProvider>
            <BrowserRouter>
              <Routes>
                {/* Public — every one of these mocks wears the accent header. */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/jasa/belanja" element={<JastipBelanjaPage />} />
                  <Route path="/jasa/belanja/terkirim/:code" element={<JastipTerkirimPage />} />
                  <Route path="/cek-harga" element={<CekHargaPage />} />
                  <Route path="/toko" element={<TokoEtalasePage />} />
                  <Route path="/toko/koleksi/:slug" element={<TokoKoleksiDetailPage />} />
                  <Route path="/toko/digital/:slug" element={<TokoDigitalDetailPage />} />
                  <Route path="/toko/titip-lisensi" element={<TokoTitipLisensiPage />} />
                  <Route path="/toko/bayar/:code" element={<TokoBayarPage />} />
                  <Route path="/order/:code" element={<LacakPage />} />
                  <Route path="/syarat" element={<SyaratPage />} />
                  <Route path="/privasi" element={<PrivasiPage />} />
                  <Route path="/refund" element={<RefundPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>

                {/* Auth */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/daftar" element={<DaftarPage />} />
                </Route>

                {/* Account */}
                <Route
                  element={
                    <RequireAuth>
                      <AccountLayout />
                    </RequireAuth>
                  }
                >
                  <Route path="/akun" element={<AkunRingkasanPage />} />
                  <Route path="/akun/pesanan" element={<AkunPesananPage />} />
                  <Route path="/akun/pesanan/:code" element={<AkunPesananDetailPage />} />
                  <Route path="/akun/penawaran" element={<AkunPenawaranListPage />} />
                  <Route path="/akun/penawaran/:code" element={<AkunPenawaranPage />} />
                  <Route path="/akun/lisensi" element={<AkunLisensiPage />} />
                  <Route path="/akun/pengaturan" element={<AkunPengaturanPage />} />
                </Route>

                {/* Admin — a non-admin gets the same 404 as an unmapped path. */}
                <Route
                  element={
                    <RequireAdmin>
                      <AdminLayout />
                    </RequireAdmin>
                  }
                >
                  <Route path="/admin" element={<AdminAntrianPage />} />
                  <Route path="/admin/pesanan" element={<AdminPesananPage />} />
                  <Route path="/admin/pesanan/:code" element={<AdminPesananDetailPage />} />
                  <Route path="/admin/penawaran" element={<AdminPenawaranListPage />} />
                  <Route path="/admin/penawaran/:code" element={<AdminPenawaranPage />} />
                  <Route path="/admin/toko" element={<AdminTokoPage />} />
                  <Route path="/admin/toko/baru" element={<AdminTokoBaruPage />} />
                  <Route path="/admin/toko/:id/edit" element={<AdminTokoEditPage />} />
                  <Route path="/admin/lisensi" element={<AdminLisensiPage />} />
                  <Route path="/admin/lisensi/kirim/:code" element={<AdminLisensiKirimPage />} />
                  <Route path="/admin/titip-lisensi" element={<AdminTitipLisensiPage />} />
                  <Route path="/admin/pembayaran/:code" element={<AdminPembayaranPage />} />
                </Route>

                {/* Old routes people have bookmarked (CONTRACT.md §2.4). */}
                <Route path="/ecommerce" element={<Navigate to="/akun/pesanan" replace />} />
                <Route path="/ecommerce/new" element={<Navigate to="/jasa/belanja" replace />} />
                <Route path="/ecommerce/estimate" element={<Navigate to="/cek-harga" replace />} />
                <Route path="/software" element={<Navigate to="/toko" replace />} />
                <Route path="/software/orders" element={<Navigate to="/akun/lisensi" replace />} />
                <Route path="/settings" element={<Navigate to="/akun/pengaturan" replace />} />
                <Route path="/register" element={<Navigate to="/daftar" replace />} />
              </Routes>
            </BrowserRouter>
          </ToastProvider>
        </NotificationProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}

export default App;
